import { defineStore, acceptHMRUpdate } from "pinia";
import {
  applyInputChildrenRules,
  normalizeDuplicableGroups,
  pruneHiddenGroups,
  stripInternalDuplicateKeys,
  validateMinCreditCounts
} from "@/composables/formDuplicable";
import { getStoredResponse } from "@/composables/responseStorage";
import { usesGoogleReviews, normalizeGoogleReviews, shouldShowGoogleReviewsDesktop } from "@/composables/googleReviews";

export const useStore = defineStore("storeId", {
  state: () => {
    return {
      apiBaseUrl: useRuntimeConfig().public.appVersion === "dev" ? window.location.origin + "/api" : useRuntimeConfig().public.apiBaseUrl,
      page: "",
      userAgent: "",
      isMobile: false,
      pending: true,
      success: false,
      activity: "RAC",
      stepNb: 0,
      sectionNb: 0,
      needs_memory: null,
      form: null,
      googleReviews: null,
      xtor: null,
      site: "",
      version: "",
      code_aff: "",
      noIntro: "false",
      response: {},
      submitResponseData: {},
      isTemporizing: false
    };
  },
  actions: {
    getLocalisation (input) {
      const encodedInput = encodeURIComponent(input);
      const cogBaseUrl = useRuntimeConfig().public.cogBaseUrl || "https://cog.finvens.io";
      const cogUrl = cogBaseUrl + "/communes?postcode=" + encodedInput;
      const geopfBaseUrl = "https://data.geopf.fr/geocodage/search?q=" + encodedInput;

      const fetchJson = (url, signal) => {
        return fetch(url, { method: "GET", signal })
          .then((res) => {
            if (!res.ok) { throw new Error(res.status.toString()); }
            return res.json();
          });
      };

      const dedupGeopf = (features) => {
        const seen = new Set();
        return features
          .filter(f => f.properties.city && f.properties.postcode)
          .reduce((acc, feature) => {
            const city = feature.properties.city;
            if (!seen.has(city)) {
              seen.add(city);
              acc.push({
                codePostal: feature.properties.postcode,
                nomCommune: city
              });
            }
            return acc;
          }, []);
      };

      const geopfFallback = () => fetchJson(geopfBaseUrl + "&type=municipality")
        .then((data) => {
          if (data.features && data.features.length > 0) {
            return dedupGeopf(data.features);
          }
          // Fallback : certains CP (ex: 31500) n'ont pas de municipality → locality
          return fetchJson(geopfBaseUrl + "&type=locality")
            .then((localityData) => {
              if (localityData.features && localityData.features.length > 0) {
                return dedupGeopf(localityData.features);
              }
              // Fallback : certains CP (ex: 34070) n'ont ni municipality ni locality → recherche sans filtre
              return fetchJson(geopfBaseUrl)
                .then((allData) => {
                  if (allData.features && allData.features.length > 0) {
                    return dedupGeopf(allData.features);
                  }
                  return [];
                });
            });
        })
        .catch(() => []);

      // Source principale : cog.finvens.io (données INSEE). Fallback : data.geopf.fr.
      // Timeout court sur cog : si l'endpoint hang, on bascule sur geopf sans
      // attendre le timeout browser (plusieurs minutes).
      const cogController = new AbortController();
      const cogTimeout = setTimeout(() => cogController.abort(), 2500);
      return fetchJson(cogUrl, cogController.signal)
        .finally(() => clearTimeout(cogTimeout))
        .then((communes) => {
          if (Array.isArray(communes) && communes.length > 0) {
            return communes.map(c => ({
              codePostal: input,
              nomCommune: c.label
            }));
          }
          return geopfFallback();
        })
        .catch(() => geopfFallback());
    },
    getJsonForm () {
      return fetch(this.apiBaseUrl + "/config/" + this.site + "/" + this.version, {
        method: "GET",
        baseURL: this.apiBaseUrl,
        headers: {
          "Content-Type": "application/json" // this shows the expected content type
        }
      })
        .then(res => res.json())
        .then((data) => {
          this.form = data.form;
          this.needs_memory = data.needs_memory;
          // Récupère la note/avis Google si la config les utilise (flag
          // top-level et/ou composant "googleReviews" dans le formulaire).
          // Non bloquant : le formulaire ne doit pas attendre les avis, le
          // composant réagit à store.googleReviews dès qu'ils arrivent.
          if (usesGoogleReviews(this.form)) {
            this.fetchGoogleReviews();
          }
        });
    },
    /**
     * Récupère la note et les avis Google (valeur pré-calculée servie par le
     * backend, générée au build). En cas d'échec/payload invalide, on masque le
     * bloc (dégradation propre) tout en laissant une trace diagnostique : le
     * warn console est capturé en breadcrumb Sentry (intégration par défaut),
     * ce qui permet de distinguer 404 / timeout / payload vide en prod.
     */
    fetchGoogleReviews () {
      // Timeout court : si l'endpoint hang, on abandonne sans laisser de
      // promesse pendante (aligné sur getAdresses). Non bloquant pour le form.
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2500);
      // Pas de Content-Type : requête sans corps, et cet en-tête déclencherait
      // un preflight CORS (le front et l'API sont sur des origines distinctes).
      return fetch(this.apiBaseUrl + "/google-reviews/" + this.site, {
        method: "GET",
        signal: controller.signal
      })
        .then((res) => {
          if (!res.ok) {
            console.warn(`[googleReviews] endpoint ${res.status} pour ${this.site}, bloc masqué`); // eslint-disable-line no-console
            return null;
          }
          return res.json();
        })
        .then((data) => {
          this.googleReviews = normalizeGoogleReviews(data);
        })
        .catch((err) => {
          console.warn(`[googleReviews] échec de récupération pour ${this.site}, bloc masqué`, err); // eslint-disable-line no-console
          this.googleReviews = null;
        })
        .finally(() => clearTimeout(timeout));
    },
    /**
     * Merge léger : sauvegarde la réponse sans pruneHiddenGroups
     * ni normalizeDuplicableGroups. Utilisé par le retour navigation.
     */
    mergeResponseLight (responseDelta = {}, propsToDelete = []) {
      const oldResponse = getStoredResponse();
      if (propsToDelete && propsToDelete.length) {
        propsToDelete.forEach((key) => {
          delete oldResponse[key];
        });
      }
      if (!responseDelta || typeof responseDelta !== "object") {
        this.response = oldResponse;
        localStorage.setItem("response", JSON.stringify(oldResponse));
        return oldResponse;
      }
      const newResponse = Object.keys(oldResponse).length
        ? { ...oldResponse, ...responseDelta }
        : { ...responseDelta };
      this.response = newResponse;
      localStorage.setItem("response", JSON.stringify(newResponse));
      return newResponse;
    },
    updateResponseFromForm (responseDelta = {}, propsToDelete = []) {
      const oldResponse = getStoredResponse();
      if (propsToDelete && propsToDelete.length) {
        propsToDelete.forEach((key) => {
          delete oldResponse[key];
        });
      }
      if (!responseDelta || typeof responseDelta !== "object") {
        this.response = oldResponse;
        localStorage.setItem("response", JSON.stringify(oldResponse));
        return oldResponse;
      }
      const newResponse = Object.keys(oldResponse).length
        ? { ...oldResponse, ...responseDelta }
        : { ...responseDelta };
      applyInputChildrenRules(this.form, newResponse, responseDelta);
      pruneHiddenGroups(this.form, newResponse);
      normalizeDuplicableGroups(this.form, newResponse);
      this.response = newResponse;
      localStorage.setItem("response", JSON.stringify(newResponse));
      return newResponse;
    },
    saveFormData () {
      const payload = stripInternalDuplicateKeys(this.response);
      return fetch(this.apiBaseUrl + "/validate", {
        method: "POST",
        baseURL: this.apiBaseUrl,
        headers: {
          "Content-Type": "application/json" // this shows the expected content type
        },
        body: JSON.stringify(payload)
      })
        .then(res => res.json())
        .then((data) => {
          return data;
        })
        .catch(() => {
          this.page = "credit-form";
          this.pending = true;
          this.success = false;
        });
    },
    submitFormData () {
      this.submitResponseData = {};
      this.success = false;
      this.pending = true;
      this.isTemporizing = true;
      setTimeout(() => {
        this.isTemporizing = false;
      }, 2000);
      const payload = stripInternalDuplicateKeys(this.response);
      if (!validateMinCreditCounts(this.form, payload)) {
        this.pending = false;
        this.isTemporizing = false;
        return Promise.reject(new Error("Payload invalide : aucun crédit renseigné"));
      }
      this.page = "result";
      return (
        fetch(this.apiBaseUrl + "/submit", {
          method: "POST",
          baseURL: this.apiBaseUrl,
          headers: {
            "Content-Type": "application/json" // this shows the expected content type
          },
          body: JSON.stringify(payload)
        })
          .then((res) => {
            if (res.ok) {
              return res.json();
            } else {
              return Promise.reject(res);
            }
          })
          .then((data) => {
            if (this.isTemporizing) {
              setTimeout(() => { this.handleSubmitResponse(true, data); }, 2000);
            } else {
              this.handleSubmitResponse(true, data);
            }
          })
          .catch((e) => {
            if (e.status === 504) {
              this.handleSubmitResponse(false, null, true);
            } else if (this.isTemporizing) {
              setTimeout(() => { this.handleSubmitResponse(); }, 2000);
            } else {
              this.handleSubmitResponse();
            }
          })
      );
    },
    handleSubmitResponse (apiSuccess = false, apiData = null, timeout = false) {
      let event = "";
      if (apiData) {
        if (apiData.resultat === "echec") {
          event = "submit_" + apiData.resultat;
        } else {
          this.success = true;
          apiData.activite && (this.activity = apiData.activite);
          event = "submit_" + apiData.resultat + "_" + apiData.activite;
        }
      }
      if (apiSuccess && apiData !== null) {
        this.page = "result";
        this.submitResponseData = apiData;
        this.pending = false;
      } else {
        this.page = "credit-form";
        this.pending = false;
      }
      if (timeout) {
        event = "submit_timeout";
        this.success = true;
        this.page = "result";
        this.pending = false;
      }
      const prospect = {};
      this.form.gtm_data_to_save.forEach((gtmData) => {
        const lookAlikeData = Object.keys(this.response)
          .filter(key => key.slice(0, -3) === gtmData)
          .reduce((acc, look) => {
            const toPush = { [look]: this.response[look] };
            acc.push(toPush);
            return acc;
          }, []);
        const value = this.response[gtmData]
          ? this.response[gtmData]
          : lookAlikeData;

        if (typeof value === "object") {
          prospect[gtmData] = !Array.isArray(value) ? value.value : Object.assign({}, ...value);
        } else {
          prospect[gtmData] = value;
        }
        if (this.success && apiData) {
          prospect.id_pnr = apiData.id_pnr;
          prospect.token_nfl = apiData.token_nfl;
          prospect.profil = apiData.profil;
        }
      });
      window.dataLayer?.push({
        event,
        prospect
      });
    }
  },
  getters: {
    getForm (state) {
      return state.form;
    },
    /**
     * Vrai dès que la donnée avis Google est disponible et exploitable.
     * Source unique consommée par le bloc en flux (mobile/les_furets) et son
     * label, pour éviter la désynchronisation flag config / donnée fetchée.
     */
    showGoogleReviews (state) {
      return !!state.googleReviews;
    },
    /**
     * Vrai quand le bloc avis Google doit s'afficher dans le stepper desktop.
     * Consommé par le bloc, la classe de mise en page et la hauteur des étapes,
     * qui doivent partager la même condition.
     */
    useGoogleReviewsDesktop (state) {
      return shouldShowGoogleReviewsDesktop(state.form, state.googleReviews, state.isMobile);
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStore, import.meta.hot));
}
